import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi } from "@/lib/useApi";
import { PageTitle, Panel, Stat, Loading, Empty, ErrorState } from "@/components/Common";
import { inr, downloadCSV, toEpoch, istDate, epochDaysAgo } from "@/lib/format";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Download } from "lucide-react";

export default function Performance() {
  const [sp, setSp] = useSearchParams();
  const defFrom = new Date(epochDaysAgo(7) * 1000).toISOString().slice(0, 10);
  const defTo = new Date().toISOString().slice(0, 10);
  const from = sp.get("from") || defFrom;
  const to = sp.get("to") || defTo;

  const setParam = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n); };

  const qs = new URLSearchParams();
  qs.set("frm", toEpoch(from));
  qs.set("to", toEpoch(to) + 86399);
  const { data, loading, error, reload } = useApi(`/merchant/performance?${qs.toString()}`);

  const exportCsv = () => {
    if (!data?.machines?.length) return;
    downloadCSV(`performance_${from}_to_${to}.csv`, data.machines.map((m) => ({
      code: m.code, name: m.name, uptime_pct: m.uptime_pct, txn_volume: m.txn_volume,
      revenue_inr: (m.revenue_paise / 100).toFixed(2), settlement_percent: m.settlement_percent ?? "",
      settlement_inr: m.settlement_paise != null ? (m.settlement_paise / 100).toFixed(2) : "",
      refund_count: m.refund_count, refund_inr: (m.refund_paise / 100).toFixed(2),
    })));
  };

  return (
    <div>
      <PageTitle title="Performance" subtitle={`Metrics from ${istDate(toEpoch(from))} to ${istDate(toEpoch(to))}`}>
        <button data-testid="export-perf-btn" onClick={exportCsv}
          className="flex items-center gap-2 rounded-sm border border-border px-3 py-1.5 text-sm hover:bg-[#1c1c1c] transition-colors">
          <Download className="h-3.5 w-3.5" /> Export CSV
        </button>
      </PageTitle>

      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground">From</label>
          <input data-testid="perf-from" type="date" value={from} onChange={(e) => setParam("from", e.target.value)}
            className="mt-1 block rounded-sm border border-input bg-[#141414] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-muted-foreground">To</label>
          <input data-testid="perf-to" type="date" value={to} onChange={(e) => setParam("to", e.target.value)}
            className="mt-1 block rounded-sm border border-input bg-[#141414] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white" />
        </div>
      </div>

      {loading && !data ? <Loading /> : error ? <ErrorState error={error} onRetry={reload} /> : !data.machines.length ? <Empty label="No machines assigned" /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <Stat label="Avg Uptime" value={`${data.aggregate.uptime_pct}%`} accent="#10B981" />
            <Stat label="Txn Volume" value={data.aggregate.txn_volume} />
            <Stat label="Revenue" value={inr(data.aggregate.revenue_paise)} accent="#10B981" />
            <Stat label="Refunds" value={`${data.aggregate.refund_count} · ${inr(data.aggregate.refund_paise)}`} accent="#F59E0B" />
          </div>
          {data.settlement_mode && (
            <div className="mb-5 flex items-center gap-3 rounded-sm border border-border bg-[#0f0f0f] px-4 py-3 text-sm" data-testid="settlement-summary">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Settlement Mode</span>
              <span className="mono px-1.5 py-0.5 rounded-sm border border-border">{data.settlement_mode}</span>
              {data.aggregate.settlement_paise != null && (
                <span className="ml-auto">Merchant settlement (est.): <span className="mono text-[#10B981]">{inr(data.aggregate.settlement_paise)}</span></span>
              )}
            </div>
          )}

          <Panel title="Revenue by Machine (INR)" className="mb-5">
            <div className="p-4" style={{ height: 280 }} data-testid="perf-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.machines.map((m) => ({ name: m.code, revenue: m.revenue_paise / 100 }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
                  <XAxis dataKey="name" stroke="#6B7280" fontSize={12} tickLine={false} />
                  <YAxis stroke="#6B7280" fontSize={12} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#141414", border: "1px solid #262626", borderRadius: 4 }}
                    formatter={(v) => [`₹${v.toLocaleString("en-IN")}`, "Revenue"]} />
                  <Bar dataKey="revenue" fill="#10B981" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel title="Per-Machine Metrics">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="perf-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="px-4 py-2 font-medium">Machine</th>
                    <th className="px-4 py-2 font-medium text-right">Uptime %</th>
                    <th className="px-4 py-2 font-medium text-right">Txns</th>
                    <th className="px-4 py-2 font-medium text-right">Revenue</th>
                    <th className="px-4 py-2 font-medium text-right">Settlement</th>
                    <th className="px-4 py-2 font-medium text-right">Refunds</th>
                  </tr>
                </thead>
                <tbody>
                  {data.machines.map((m) => (
                    <tr key={m.machine_id} className="border-b border-border/60" data-testid={`perf-row-${m.code}`}>
                      <td className="px-4 py-2.5"><span className="mono">{m.code}</span> <span className="text-muted-foreground text-xs">{m.name}</span></td>
                      <td className="px-4 py-2.5 mono text-right" style={{ color: m.uptime_pct >= 95 ? "#10B981" : m.uptime_pct >= 80 ? "#F59E0B" : "#EF4444" }}>{m.uptime_pct}%</td>
                      <td className="px-4 py-2.5 mono text-right">{m.txn_volume}</td>
                      <td className="px-4 py-2.5 mono text-right">{inr(m.revenue_paise)}</td>
                      <td className="px-4 py-2.5 mono text-right text-[#10B981]">{m.settlement_paise != null ? inr(m.settlement_paise) : "—"}{m.settlement_percent != null ? <span className="text-muted-foreground text-xs"> ({m.settlement_percent}%)</span> : null}</td>
                      <td className="px-4 py-2.5 mono text-right">{m.refund_count} · {inr(m.refund_paise)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
